import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { Observable, firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ViewDraftEmailPopupComponent } from 'src/app/shared/components/view-draft-email-popup/view-draft-email-popup.component';
import { ConfirmPopupComponent } from 'src/app/modules/confirm-popup/confirm-popup.component';

export type PortMailType = 'eosp_port_call' | 'cosp_cargo_docs';
export type PortMailOperation = 'loading' | 'discharging';
/** approval: offered after Approve; manual: asked for from the form or the Report Desk. */
export type PortMailSource = 'approval' | 'manual';

export interface PortMailCheck {
  success: boolean;
  eligible: boolean;
  reason?: string;
  report_id?: number;
  mail_type?: PortMailType;
  mail_label?: string;
  event?: string;
  vessel_name?: string;
  imo?: string;
  client?: string;
  is_mr?: boolean;
  port_name?: string | null;
  reported_at?: string;
  operation?: PortMailOperation;
  /** null when the reports do not say; the popup then asks. */
  operation_detected?: PortMailOperation | null;
  documents_by_operation?: Record<PortMailOperation, string[]>;
  to?: string | null;
  cc?: string;
  previous_mails?: PortMailRow[];
}

export interface PortMailRow {
  id: number;
  report_id: number;
  root_id?: number;
  mail_type: PortMailType;
  mail_label?: string;
  operation: PortMailOperation | null;
  vessel_name?: string;
  imo_number?: string;
  client_name?: string;
  event_type?: string;
  report_date?: string | null;
  port_name?: string | null;
  documents?: string[];
  email_status: 'Drafted' | 'Sent' | 'Failed' | 'Skipped';
  email_subject?: string | null;
  recipient_email?: string | null;
  cc_email?: string | null;
  comment?: string | null;
  source: PortMailSource;
  drafted_by?: string | null;
  drafted_at: string | null;
  sent_at: string | null;
  send_error?: string | null;
  created_at: string;
}

export interface PortMailVessel {
  imo: string;
  vessel_name: string;
  client: string;
  is_mr: boolean;
  eosp?: { report_id: number; report_date: string | null };
  cosp?: { report_id: number; report_date: string | null };
}

/** Outcome of runFlow. */
export type PortMailFlowResult = 'drafted' | 'skipped' | 'cancelled' | 'ineligible' | 'failed';

/**
 * Port call mails to the Master (EOSP: documents for this port call; COSP: cargo documents of the
 * call just completed). EmzOil and Elite tankers only; the server decides who qualifies.
 */
@Injectable({ providedIn: 'root' })
export class PortMailService {
  private readonly base = `${environment.apiBasePcUrl}/port-consumption/report-desk/port-mails`;

  constructor(private http: HttpClient) {}

  check(reportId: number | string, mailType?: PortMailType): Observable<PortMailCheck> {
    let params = new HttpParams().set('report_id', String(reportId));
    if (mailType) params = params.set('mail_type', mailType);
    return this.http.get<PortMailCheck>(`${this.base}/check`, { params });
  }

  preview(body: { report_id: number | string; mail_type?: PortMailType; operation?: PortMailOperation }): Observable<any> {
    return this.http.post<any>(`${this.base}/preview`, body);
  }

  draft(body: any): Observable<any> {
    return this.http.post<any>(`${this.base}/draft`, body);
  }

  skip(body: { report_id: number | string; mail_type?: PortMailType; operation?: PortMailOperation }): Observable<any> {
    return this.http.post<any>(`${this.base}/skip`, body);
  }

  list(params: Record<string, string>): Observable<{ success: boolean; mails: PortMailRow[]; counts: Record<string, number> }> {
    return this.http.get<any>(this.base, { params });
  }

  vessels(client?: string): Observable<{ success: boolean; vessels: PortMailVessel[] }> {
    const params: Record<string, string> = client ? { client } : {};
    return this.http.get<any>(`${this.base}/vessels`, { params });
  }

  /**
   * Open the Draft Email popup with the port call mail for a report (cargo operation preset from
   * the reports and switchable there) and draft it into the operations@ mailbox (saved to Drafts,
   * never sent). If the draft cannot be saved, an alert says why and offers to try again. After an
   * approval a Cancel is logged as Skipped so the
   * Report Desk shows which port calls got no mail. Ineligible reports end quietly after an
   * approval and with a note when asked for by hand.
   */
  async runFlow(
    dialog: MatDialog,
    toastr: ToastrService,
    reportId: number | string,
    source: PortMailSource,
    mailType?: PortMailType,
  ): Promise<PortMailFlowResult> {
    let check: PortMailCheck;
    try {
      check = await firstValueFrom(this.check(reportId, mailType));
    } catch (err: any) {
      if (source === 'manual') toastr.error(err?.error?.error || 'Could not check the port call mail.', 'Port Call Mail');
      return 'failed';
    }
    if (!check?.eligible) {
      if (source === 'manual') toastr.info(check?.reason || 'No port call mail for this report.', 'Port Call Mail');
      return 'ineligible';
    }

    let operation: PortMailOperation = check.operation || 'discharging';
    const skip = () => {
      if (source === 'approval') {
        this.skip({ report_id: reportId, mail_type: check.mail_type, operation }).subscribe({ error: () => {} });
      }
    };

    let preview: any;
    try {
      preview = await firstValueFrom(this.preview({ report_id: reportId, mail_type: check.mail_type, operation }));
    } catch (err: any) {
      toastr.error(err?.error?.error || 'Could not prepare the mail.', 'Port Call Mail');
      return 'failed';
    }

    const previous = check.previous_mails ?? [];
    const fmt = (iso: string | null) =>
      iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
    const edited: any = await firstValueFrom(
      dialog
        .open(ViewDraftEmailPopupComponent, {
          width: '95vw',
          maxWidth: '1200px',
          maxHeight: '94vh',
          panelClass: 'view-draft-email-dialog-panel',
          data: {
            to: preview.to,
            cc: preview.cc,
            noDefaultCc: true,
            from: preview.from,
            subject: preview.subject,
            html: preview.html,
            vesselName: check.vessel_name,
            heading: check.mail_type === 'eosp_port_call' ? 'Draft Port Call Email' : 'Draft Cargo Documents Email',
            badge: preview.mail_label,
            canDraft: true,
            operation,
            operationDetected: !!check.operation_detected,
            loadOperation: async (op: PortMailOperation) => {
              const fresh = await firstValueFrom(this.preview({ report_id: reportId, mail_type: check.mail_type, operation: op }));
              return { subject: fresh.subject, html: fresh.html };
            },
            notice: previous.length
              ? 'Already logged: ' + previous.map((m) => `${m.email_status} ${fmt(m.drafted_at || m.created_at)}`).join(' · ')
              : undefined,
          },
        })
        .afterClosed(),
    );
    if (edited?.operation) operation = edited.operation;
    if (!edited || (edited.action !== 'send' && edited.action !== 'draft')) {
      skip();
      return 'cancelled';
    }

    for (;;) {
      try {
        await firstValueFrom(
          this.draft({
            report_id: reportId,
            mail_type: check.mail_type,
            operation,
            to: edited.to,
            cc: edited.cc,
            subject: edited.subject,
            html: edited.html,
            comment: edited.comment,
            source,
          }),
        );
        toastr.success(`${preview.mail_label} saved to ${preview.from || 'operations@'} Drafts ✉️`, 'Mail Drafted ✅');
        return 'drafted';
      } catch (err: any) {
        const retry = await this.draftFailedAlert(dialog, err, preview.from);
        if (!retry) return 'failed';
      }
    }
  }

  /** Explains in plain words why the draft was not saved; resolves true for "Try again". */
  private async draftFailedAlert(dialog: MatDialog, err: any, mailbox?: string): Promise<boolean> {
    const box = mailbox || 'operations@merakishippingservices.com';
    const code = err?.error?.error_code;
    const message =
      code === 'mailbox_login_failed'
        ? `We couldn't sign in to <b>${box}</b>, so the email was not saved to Drafts. ` +
          'Its password has probably been changed. Please ask the admin to update the mailbox password, then try again.'
        : code === 'mailbox_not_configured'
          ? `The password for <b>${box}</b> has not been set up yet, so the email could not be saved to Drafts. ` +
            'Please ask the admin to add it, then try again.'
          : 'Something went wrong while saving the email to Drafts. Nothing was sent to the vessel. ' +
            'Please check your connection and try again in a moment.';
    return !!(await firstValueFrom(
      dialog
        .open(ConfirmPopupComponent, {
          width: '460px',
          disableClose: true,
          panelClass: 'custom-dialog-container',
          data: {
            mainTitle: 'Email not drafted',
            title: message,
            yesText: 'Try again',
            yesIcon: 'refresh',
            noText: 'Close',
            iconClass: 'mark_email_unread',
            isDanger: true,
          },
        })
        .afterClosed(),
    ));
  }
}
